"use client"

import { dataset, projectId } from "@/sanity/env"
import type { SanityDocument } from "@sanity/client"
import { createDataAttribute } from "@sanity/visual-editing"
import { useOptimistic } from "@sanity/visual-editing/react"

type DraggableItem = {
    key: string
    className: string
    element: React.ReactNode
}

type Props = {
    documentId: string
    documentType: string
    contentField: string
    items: DraggableItem[]
}

export default function DraggableContentList({
    documentId,
    documentType,
    contentField,
    items: initialItems
}: Props) {
    const id = documentId.replace(/^drafts\./, "")
    const attr = { projectId, dataset, baseUrl: "/admin", id, type: documentType }

    const items = useOptimistic<DraggableItem[], SanityDocument>(
        initialItems,
        (current, action) => {
            if (action.id !== id) return current
            const updated = action.document[contentField]
            if (!Array.isArray(updated)) return current
            return updated
                .map((s: { _key?: string }) => current?.find(i => i.key === s._key))
                .filter((i): i is DraggableItem => !!i)
        }
    )

    if (!items?.length) return null

    return (
        <div
            data-sanity={createDataAttribute({ ...attr, path: contentField }).toString()}
        >
            {items.map(item => (
                <div
                    key={item.key}
                    className={item.className}
                    data-sanity={createDataAttribute({
                        ...attr,
                        path: `${contentField}[_key=="${item.key}"]`
                    }).toString()}
                >
                    {item.element}
                </div>
            ))}
        </div>
    )
}
